/**
 * Display formatters for the Report Queue. Plain module (no components) so the
 * component files stay clean under react-refresh/only-export-components.
 *
 * All job timestamps are epoch-ms INTEGERS, not ISO strings.
 */

/** "2.6s" / "1m 48s" / "6m 00s". */
export function fmtDuration(ms) {
  if (ms == null || !Number.isFinite(ms)) return '—'
  const s = ms / 1000
  if (s < 60) return `${s < 10 ? s.toFixed(1) : Math.round(s)}s`
  const m = Math.floor(s / 60)
  const rem = Math.round(s % 60)
  return `${m}m ${String(rem).padStart(2, '0')}s`
}

/**
 * "0:01" / "1:12" / "6:00" — a live mm:ss clock for a run still in flight.
 *
 * Distinct from `fmtDuration` on purpose, and the difference is not cosmetic.
 * `fmtDuration` describes a FINISHED span and optimises for reading a number
 * ("2.6s", "1m 48s"); it changes shape as it crosses ten seconds and again at a
 * minute, which is fine once but jitters badly at 1Hz. A clock keeps a stable
 * width and a stable meaning while it counts, so the eye can ignore it until it
 * wants it. Himaad's Run and tray frames use this form ("0:01", "0:04"); the
 * queue table's Duration column uses `fmtDuration`.
 *
 * Floors rather than rounds — a run that has been going 1.9s has not yet been
 * going 2 seconds, and a clock that reads ahead of the wall is a small lie on a
 * surface whose whole job is saying how long you have been waiting.
 */
export function fmtClock(ms) {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return '0:00'
  const total = Math.floor(ms / 1000)
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}

/**
 * "in 9 hours" / "in 4 days" / "in 12 minutes" / "in under a minute".
 *
 * For a FUTURE instant, when the useful thing is the distance rather than the
 * clock time — the queue's "next scheduled run in 9 hours". Coarse on purpose:
 * one unit, no "9 hours 14 minutes", because the reader is deciding whether to
 * wait or leave, not setting an alarm.
 *
 * Returns null for a past or missing instant rather than "in -3 hours". The
 * caller then renders nothing, which is right: a `nextRunAt` behind the wall
 * clock means the cron has not caught up, not that a run is imminent.
 */
export function fmtInFuture(ms, now = Date.now()) {
  if (ms == null || !Number.isFinite(ms)) return null
  const delta = ms - now
  if (delta <= 0) return null
  const mins = Math.round(delta / 60_000)
  if (mins < 1) return 'in under a minute'
  if (mins < 60) return `in ${mins} ${mins === 1 ? 'minute' : 'minutes'}`
  const hours = Math.round(delta / 3_600_000)
  if (hours < 24) return `in ${hours} ${hours === 1 ? 'hour' : 'hours'}`
  const days = Math.round(delta / 86_400_000)
  return `in ${days} ${days === 1 ? 'day' : 'days'}`
}

/** "2:59 PM" */
export function fmtTime(ms) {
  if (ms == null) return '—'
  try { return new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) } catch { return '—' }
}

/** "Today 2:59 PM" / "Yesterday 4:02 PM" / "Aug 3, 7:00 AM" */
export function fmtWhen(ms, now = Date.now()) {
  if (ms == null) return '—'
  try {
    const d = new Date(ms)
    const today = new Date(now)
    const sameDay = d.toDateString() === today.toDateString()
    const yesterday = new Date(now - 86_400_000).toDateString() === d.toDateString()
    if (sameDay) return `Today ${fmtTime(ms)}`
    if (yesterday) return `Yesterday ${fmtTime(ms)}`
    return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${fmtTime(ms)}`
  } catch { return '—' }
}

/** "in 21h" / "in 45m" / "expired". Countdown for a result's remaining life. */
export function fmtExpiresIn(expiresAt, now = Date.now()) {
  if (expiresAt == null) return null
  const ms = expiresAt - now
  if (ms <= 0) return 'expired'
  const hours = Math.floor(ms / 3_600_000)
  if (hours >= 24) return `in ${Math.floor(hours / 24)}d`
  if (hours >= 1) return `in ${hours}h`
  return `in ${Math.max(1, Math.round(ms / 60_000))}m`
}

/**
 * "Aug 10, 7:00 AM CDT" — a timestamp rendered in a SPECIFIC zone.
 *
 * Schedules fire at a local wall time that is the schedule's, not the viewer's.
 * Rendering "next run" in the browser's zone next to a rule that reads "Weekly on
 * Monday at 07:00" produced a row that contradicted itself (7:00 vs 8:00 AM for a
 * Chicago schedule viewed from New York). The zone abbreviation is included so
 * the number is never ambiguous.
 */
export function fmtWhenInZone(ms, tz) {
  if (ms == null) return '—'
  try {
    return new Date(ms).toLocaleString([], {
      timeZone: tz || undefined,
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
      timeZoneName: 'short',
    })
  } catch {
    // An unknown/invalid IANA zone must not blank the cell.
    return fmtWhen(ms)
  }
}

/**
 * "7:00 AM" — a time rendered in a SPECIFIC zone, no date.
 *
 * Same reasoning as `fmtWhenInZone`, and it is not optional: a schedule fires at
 * ITS OWN local wall time. Rendering a Chicago 07:00 schedule with the browser's
 * clock puts "8:00 AM" on the calendar directly beneath a row that says
 * "7:00 AM CDT" — one screen contradicting itself about when a report runs.
 */
export function fmtTimeInZone(ms, tz) {
  if (ms == null) return '—'
  try {
    return new Date(ms).toLocaleTimeString([], { timeZone: tz || undefined, hour: 'numeric', minute: '2-digit' })
  } catch {
    return fmtTime(ms)
  }
}

/** "4,519" with thousands separators; em-dash when absent. */
export function fmtRows(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—'
  return Number(n).toLocaleString()
}

/**
 * "1 row" / "0 rows" / "4,519 rows" / "— rows" — a row COUNT with its noun.
 *
 * Spec 167: every queue surface built this as `${fmtRows(n)} rows`, so a run that
 * returned one row read "1 rows" in the bell, the drawer, its expired panel and the
 * schedules list (seen live on QA, 2026-10-12). One producer, so the noun cannot
 * disagree with the number anywhere it is printed.
 *
 * Singular for exactly 1 and nothing else: an unknown count keeps the plural ("— rows",
 * as before), because there is no number for the noun to agree with.
 */
export function fmtRowCount(n) {
  return `${fmtRows(n)} ${n != null && Number(n) === 1 ? 'row' : 'rows'}`
}

/** Elapsed wall-clock since a job started, for a still-running job. */
export function fmtElapsed(createdAt, now = Date.now()) {
  if (createdAt == null) return null
  return fmtDuration(Math.max(0, now - createdAt))
}

/**
 * "Thu, Sep 3" — a day-COLUMN header for the schedule calendar.
 *
 * The calendar's header read `Thu 3` (QA, 2026-09-03), and the missing month is
 * not cosmetic: that panel's window is `startOfDay(now)` plus seven days — a
 * ROLLING week, not a calendar one — so in the last week of every month the row
 * reads "… Tue 29, Wed 30, Thu 1, Fri 2 …" and the reader has to work out where
 * the month turned. On a grid whose whole job is "do heavy reports stack up on the
 * same morning", a date you have to reconstruct is the wrong kind of work.
 *
 * ## Why a new formatter rather than one of the four above
 *
 * - `fmtWhen` substitutes "Today"/"Yesterday" and appends a time — on column 0
 *   that would read "Today 12:00 AM", a meaningless midnight.
 * - `fmtWhenInZone` / `fmtTimeInZone` take a SCHEDULE's timezone. A column header
 *   must not have one: the columns are the viewer's calendar days, bucketed by
 *   `startOfDay` in the viewer's frame, and only the entry times inside a cell are
 *   rendered in their own schedule's zone.
 * - `fmtDisplayDate` is `MM/DD/YYYY` and its own header warns it off adjacent
 *   paths. Seven columns of `09/03/2026` at 10.5px does not fit the 7-up grid, and
 *   spec 100 deliberately kept the queue's short/relative forms out of that rule.
 *
 * ## Why not the literal "Sept 4th" that was asked for
 *
 * `month: 'short'` yields **"Sep"**, never "Sept" — en-US Intl and dayjs `MMM`
 * both emit the three-letter form (only en-GB says "Sept"), so "Sept" would have
 * to be hand-built. And no date anywhere in this app carries an ordinal: this very
 * subsystem says "day 15", not "the 15th" (`recurrence.js`), and dayjs renders
 * `Do` as a literal "4o" without a plugin nothing registers. Read as "give me the
 * month and the date", which this does, in the short form the sibling formatters
 * above already use.
 *
 * NO `timeZone` option, deliberately — see the second bullet.
 */
export function fmtDayColumn(ms) {
  if (ms == null) return '—'
  try {
    return new Date(ms).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })
  } catch {
    return '—'
  }
}

/**
 * "Sep 3 – Sep 9" / "Aug 28 – Sep 3" — the calendar's window, spelled out.
 *
 * The panel computed `week.from`/`week.to` and threw them away, so nothing on
 * screen anchored the seven columns to a month at all. This is the half that makes
 * the panel honest about its window rather than merely more legible, and it is
 * what disambiguates a rolling week that straddles a month or a year.
 */
export function fmtDayRange(fromMs, toMs) {
  if (fromMs == null || toMs == null) return ''
  try {
    const opts = { month: 'short', day: 'numeric' }
    const a = new Date(fromMs).toLocaleDateString([], opts)
    // `to` is the EXCLUSIVE end of the window (from + 7 days), so the last day
    // shown is the one before it. Printing `to` itself would name a day that has
    // no column.
    const b = new Date(toMs - 1).toLocaleDateString([], opts)
    return `${a} – ${b}`
  } catch {
    return ''
  }
}
